import { 
  Brain, Heart, Shield, Users, Zap, Activity, 
  MessageCircle, Calendar, Settings, TrendingUp, Target, Globe 
} from 'lucide-react';
import { ProductConfigs } from './types';

export const mentalHealthSolutionsConfigs: ProductConfigs = {
  'mental-health-practice-management': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI-Powered Mental Health Assessment',
        description: 'Advanced AI algorithms analyze patient responses to provide comprehensive mental health scoring and risk assessment',
        metrics: '95% assessment accuracy',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Heart,
        title: 'Personalized Treatment Planning',
        description: 'Intelligent treatment plan generation based on patient history, symptoms, and evidence-based therapy protocols',
        metrics: 'Personalized care plans',
        color: 'from-pink-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Integrated Care Team Coordination',
        description: 'Seamless collaboration between psychiatrists, therapists, case managers, and family members with secure communication',
        metrics: 'Complete care coordination',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Activity,
        title: 'Real-Time Wellness Monitoring',
        description: 'Continuous patient wellness tracking with mood monitoring, medication adherence, and crisis intervention alerts',
        metrics: 'Real-time wellness tracking',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: MessageCircle,
        title: 'HIPAA-Compliant Secure Messaging',
        description: 'Encrypted communication platform with video conferencing, secure file sharing, and emergency contact protocols',
        metrics: 'Secure communication',
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: Calendar,
        title: 'Intelligent Session Scheduling',
        description: 'AI-optimized appointment scheduling with therapy session reminders, waitlist management, and no-show predictions',
        metrics: '85% appointment efficiency',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Outcome-Based Analytics',
        description: 'Comprehensive therapy outcome tracking with progress visualization and evidence-based improvement metrics',
        metrics: 'Measurable outcomes',
        color: 'from-orange-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'Crisis Intervention & Prevention',
        description: 'Automated crisis detection with machine learning algorithms and immediate escalation protocols for emergency situations',
        metrics: 'Crisis prevention system',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Brain,
        title: 'Cognitive Behavioral Therapy Tools',
        description: 'Interactive CBT worksheets, mood tracking, thought records, and behavioral activation tools with progress analytics',
        metrics: 'CBT therapy tools',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Telehealth & Virtual Reality Therapy',
        description: 'Advanced telehealth platform with VR therapy sessions, biometric monitoring, and immersive treatment environments',
        metrics: 'VR therapy integration',
        color: 'from-teal-500 to-teal-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Mental Health Analytics',
        description: 'Machine learning models predict treatment outcomes, relapse risks, and optimal therapy interventions',
        metrics: 'Predictive analytics',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Language & Cultural Adaptation',
        description: 'Culturally adapted assessments and treatment protocols with multi-language support and cultural competency tools',
        metrics: 'Cultural adaptation',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Users,
        title: 'Family & Support Network Integration',
        description: 'Comprehensive family therapy tools with support network mapping and collaborative treatment planning',
        metrics: 'Family integration',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Heart,
        title: 'Trauma-Informed Care Protocols',
        description: 'Specialized trauma-informed treatment workflows with EMDR session tracking and trauma recovery analytics',
        metrics: 'Trauma-informed care',
        color: 'from-pink-500 to-pink-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'HIPAA & Mental Health Privacy',
        description: 'Advanced privacy protection specifically designed for mental health records with enhanced confidentiality controls',
        metrics: 'Mental health privacy',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Crisis Response',
        description: 'Sub-second crisis alert processing with automated emergency contact notifications and intervention protocols',
        metrics: '<1s crisis response',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Mental Health Platform',
        description: 'Enterprise-grade infrastructure supporting unlimited patients with auto-scaling and 99.99% uptime guarantee',
        metrics: 'Unlimited patient scale',
        color: 'from-emerald-500 to-emerald-600'
      },
      {
        icon: Brain,
        title: 'AI Ethics & Bias Prevention',
        description: 'Ethical AI implementation with bias detection, algorithmic fairness monitoring, and transparent decision-making',
        metrics: 'Ethical AI standards',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'High-Performance Wellness Analytics',
        description: 'Real-time wellness data processing with instant insights and immediate intervention recommendations',
        metrics: 'Real-time wellness analytics',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Globe,
        title: 'International Compliance Standards',
        description: 'Multi-jurisdictional compliance with international mental health standards and cross-border data protection',
        metrics: 'Global compliance',
        color: 'from-cyan-500 to-cyan-600'
      }
    ]
  }
};